import React, { useState, useRef, useCallback } from 'react';
import type { AnnotationTool } from '../types';
import { Menu } from '../../components/Menu';
import type { MenuItem, MenuItemRadio } from '../../components/Menu';

interface ToolDropdownProps {
  activeTool: AnnotationTool;
  onToolChange: (tool: AnnotationTool) => void;
}

// Inline SVG icons for each tool — 16×16 viewBox, stroke-based
const BoxIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const CircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const LineIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="2" y1="14" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <polyline points="7,2.5 13.5,2.5 13.5,9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const FreeformIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 12C4 6 6 10 8 6C10 2 12 8 14 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TextIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="3" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="8" y1="3" x2="8" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="5" y1="13" x2="11" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const EraserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 2.5L14 7L8 13H4L2 11L9.5 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="6" y1="6.5" x2="10.5" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const TOOL_ICONS: Record<AnnotationTool, React.FC> = {
  box: BoxIcon,
  circle: CircleIcon,
  line: LineIcon,
  arrow: ArrowIcon,
  freeform: FreeformIcon,
  text: TextIcon,
  eraser: EraserIcon,
};

interface ToolEntry {
  value: AnnotationTool;
  label: string;
  shortcut: string;
}

const TOOLS: ToolEntry[] = [
  { value: 'box', label: 'Box', shortcut: 'B' },
  { value: 'circle', label: 'Circle', shortcut: 'O' },
  { value: 'line', label: 'Line', shortcut: 'L' },
  { value: 'arrow', label: 'Arrow', shortcut: 'A' },
  { value: 'freeform', label: 'Freeform', shortcut: 'F' },
  { value: 'text', label: 'Text', shortcut: 'T' },
  { value: 'eraser', label: 'Eraser', shortcut: 'E' },
];

const TOOL_LABELS: Record<AnnotationTool, string> = {
  box: 'Box',
  circle: 'Circle',
  line: 'Line',
  arrow: 'Arrow',
  freeform: 'Freeform',
  text: 'Text',
  eraser: 'Eraser',
};

const ToolIcon = ({ tool }: { tool: AnnotationTool }) => {
  const Icon = TOOL_ICONS[tool];
  return <Icon />;
};

export const ToolDropdown = React.memo(function ToolDropdown({
  activeTool,
  onToolChange,
}: ToolDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const items: MenuItem[] = TOOLS.map((tool): MenuItemRadio => ({
    type: 'radio',
    id: tool.value,
    label: tool.label,
    icon: <ToolIcon tool={tool.value} />,
    checked: tool.value === activeTool,
    onClick: () => {
      onToolChange(tool.value);
      setIsOpen(false);
    },
  }));

  const ActiveIcon = TOOL_ICONS[activeTool];

  return (
    <div className="kollab-annotation-tool-dropdown">
      <button
        ref={triggerRef}
        className="kollab-annotation-toolbar-btn kollab-annotation-tool-trigger"
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Tool: ${TOOL_LABELS[activeTool]}`}
        data-tooltip="Drawing Tool"
        type="button"
      >
        <span className="kollab-annotation-tool-trigger-icon"><ActiveIcon /></span>
        <span className="kollab-annotation-tool-trigger-label">{TOOL_LABELS[activeTool]}</span>
        <span className="kollab-annotation-tool-trigger-arrow" aria-hidden="true" />
      </button>
      <Menu
        items={items}
        isOpen={isOpen}
        onClose={close}
        ariaLabel="Drawing tools"
        triggerRef={triggerRef}
        className="kollab-annotation-tool-menu"
      />
    </div>
  );
});
